import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { X, ArrowUpRight, Percent, Check } from "lucide-react";
import { api } from "@/lib/api";

const STORAGE_KEY = "ohdo-newsletter-popup";
const IDLE_MS = 30_000; // show after 30s of navigation

/**
 * Discreet opt-in popup with the 5% discount hook.
 * Triggers after 30s on any public page OR on exit intent (mouse leaves top).
 * Never shows on /admin, /orcamento, /unsubscribe, /privacidade or after dismissal.
 * Persists in localStorage so it doesn't reappear across sessions.
 */
export default function NewsletterPopup() {
    const [open, setOpen] = useState(false);
    const [email, setEmail] = useState("");
    const [rgpd, setRgpd] = useState(false);
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);
    const armed = useRef(false);
    const location = useLocation();

    // Bail out on admin and other opt-out pages so the popup never gets in the way.
    const blockedPath =
        location.pathname.startsWith("/admin") ||
        location.pathname.startsWith("/parceiros") ||
        location.pathname.startsWith("/cliente/") ||
        location.pathname.startsWith("/orcamento") ||
        location.pathname.startsWith("/unsubscribe") ||
        location.pathname.startsWith("/privacidade") ||
        location.pathname.startsWith("/cookies");

    useEffect(() => {
        if (blockedPath) return;
        // Already dismissed / subscribed / snoozed?
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (raw) {
                const data = JSON.parse(raw);
                // Snoozes for 30 days after dismissal, forever after subscribe.
                if (data.subscribed) return;
                if (data.dismissedAt && Date.now() - data.dismissedAt < 30 * 24 * 60 * 60 * 1000) return;
            }
        } catch { /* ignore */ }

        armed.current = true;

        const trigger = () => {
            if (!armed.current) return;
            armed.current = false;
            setOpen(true);
        };
        const timer = setTimeout(trigger, IDLE_MS);
        const onLeave = (e) => {
            // exit intent — mouse leaves through the top
            if (e.clientY <= 0) trigger();
        };
        document.addEventListener("mouseleave", onLeave);
        return () => {
            clearTimeout(timer);
            document.removeEventListener("mouseleave", onLeave);
        };
    }, [blockedPath, location.pathname]);

    const remember = (patch) => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            const data = raw ? JSON.parse(raw) : {};
            localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...data, ...patch }));
        } catch { /* ignore */ }
    };

    const close = () => {
        setOpen(false);
        remember({ dismissedAt: Date.now() });
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!email || !email.includes("@")) return toast.error("Introduza um email válido");
        if (!rgpd) return toast.error("Aceite a política de privacidade");
        setLoading(true);
        try {
            const { data } = await api.post("/newsletter/subscribe", { email, source: "popup", rgpd_consent: true });
            if (data.already) toast.info("Já se encontra subscrito. Obrigado!");
            else toast.success("Subscrição feita — verifique o seu email.");
            setDone(true);
            remember({ subscribed: true });
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Erro ao subscrever. Tente novamente.");
        } finally { setLoading(false); }
    };

    if (!open || blockedPath) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-3 sm:p-6 bg-stone-950/50 backdrop-blur-sm" onClick={close} data-testid="newsletter-popup">
            <div className="relative w-full max-w-lg bg-[hsl(var(--cream))] rounded-3xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
                <button onClick={close} className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/70 hover:bg-white flex items-center justify-center text-stone-500 hover:text-stone-800 transition z-10" data-testid="popup-close" aria-label="Fechar">
                    <X className="w-4 h-4" />
                </button>

                <div className="bg-[hsl(var(--forest))] text-white p-8 sm:p-10">
                    <div className="flex items-center gap-3 mb-5">
                        <div className="w-11 h-11 rounded-full bg-[hsl(var(--copper))] flex items-center justify-center">
                            <Percent className="w-5 h-5" />
                        </div>
                        <div className="text-[11px] uppercase tracking-widest font-mono text-white/70">Oferta de boas-vindas</div>
                    </div>
                    <h2 className="font-display text-3xl sm:text-4xl font-bold leading-[1.05]">
                        <span className="font-serif-italic text-[hsl(var(--copper))]">5% desconto</span><br />
                        na sua primeira obra connosco.
                    </h2>
                    <p className="text-white/80 mt-3 text-sm">
                        Subscreva a nossa newsletter e receba conteúdos sobre obras, novidades e o cupão para aplicar ao seu primeiro orçamento.
                    </p>
                </div>

                <div className="p-6 sm:p-8">
                    {done ? (
                        <div className="text-center py-4">
                            <div className="w-12 h-12 rounded-full bg-emerald-100 text-emerald-700 mx-auto flex items-center justify-center mb-3">
                                <Check className="w-6 h-6" />
                            </div>
                            <p className="font-display font-bold text-lg text-stone-900">Está feito.</p>
                            <p className="text-sm text-stone-600 mt-1">Enviámos-lhe um email de confirmação com o próximo passo.</p>
                        </div>
                    ) : (
                        <form onSubmit={submit} className="space-y-3" data-testid="popup-form">
                            <input
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="o.seu.email@exemplo.pt"
                                className="w-full bg-white border border-stone-300 rounded-full px-5 py-3 text-sm text-stone-800 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-[hsl(var(--copper))]"
                                data-testid="popup-email"
                                autoComplete="email"
                                autoFocus
                            />
                            <label className="flex items-start gap-2 text-xs text-stone-600 leading-snug">
                                <input type="checkbox" checked={rgpd} onChange={(e) => setRgpd(e.target.checked)} className="mt-0.5 accent-[hsl(var(--copper))]" data-testid="popup-rgpd" />
                                <span>Aceito receber a newsletter e a política de privacidade. Sem spam. Pode cancelar a subscrição a qualquer momento.</span>
                            </label>
                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full btn-copper !justify-center disabled:opacity-50"
                                data-testid="popup-submit"
                            >
                                {loading ? "A enviar…" : "Quero os 5% de desconto"}
                                <ArrowUpRight className="w-4 h-4" />
                            </button>
                            <button type="button" onClick={close} className="w-full text-xs text-stone-500 hover:text-stone-800 transition py-1" data-testid="popup-dismiss">
                                Não obrigado, prefiro continuar a navegar
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
}
